import { ComponentType } from '@angular/cdk/overlay';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal, untracked } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MemberProfilesService } from '@lib/members/data-access';
import { convertDraftsToItems, selectPrescriptionDrafts } from '@lib/prescriptions/data-access';
import {
  GuaranteeApplicationsService,
  refreshPrescriptionsData,
  savePrescription,
  selectPrescriptionsFilters,
  selectPrescriptionsGuarantees,
  selectTargetPrescription,
} from '@lib/prescriptions/data-access';
import { DiagnosisResponse, GuaranteeApplication, GuaranteeApplicationStatus, GuaranteeCreatorTypeString, GuaranteeStatus } from '@lib/prescriptions/data-access';
import {
  GenericServiceValue,
  PersonalDoctorValue,
  PharmacologicalTreatmentValue,
  Prescription,
  PrescriptionFormValue,
  PrescriptionPlanningStatus,
  PrescriptionStatus,
  PrescriptionTypes,
  isVisiblePrescription,
} from '@lib/prescriptions/data-access';
import {
  CreateLaboratoryOrderModalComponent,
  CreateLaboratoryOrderModalData,
  DiagnosisComponent,
  FreeFormModalComponent,
  MedicationPrescriptionFormModalComponent,
  PersonalDoctorPrescriptionFormModalComponent,
  ServicePrescriptionFormModalComponent,
} from '@lib/prescriptions/ui';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { of, switchMap, take, tap } from 'rxjs';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { AppointmentEventPlaceType, CreateAppointmentModalType } from '@lib/core/models/create-appointment.interface';
import { AppointmentTypes } from '@lib/core/models/visit.interface';

import { InfoBadgeColors, InfoBadgeComponent } from '@lib/shared-ui/commons/info-badge/info-badge.component';
import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';
import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { PrescriptionItemComponent } from '../../prescriptions-list/prescription-item/prescription-item.component';

@Component({
  selector: 'prescriptions-guarantees-list',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DiagnosisComponent,
    PrescriptionItemComponent,
    MatIcon,
    StatusComponent,
    NgOptimizedImage,
    LoaderComponent,
    InfoBadgeComponent,
    TranslatePipe,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    NgClass,
  ],
  templateUrl: './guarantees-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuaranteesListComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly store = inject(Store);
  private readonly guaranteesService = inject(GuaranteeApplicationsService);
  private readonly profileService = inject(MemberProfilesService);

  protected readonly creatorType = GuaranteeCreatorTypeString;
  protected readonly guaranteeStatuses = GuaranteeApplicationStatus;
  protected readonly badgeColors = InfoBadgeColors;
  protected readonly prescriptionTypes = PrescriptionTypes;
  protected readonly formatDate = formatDate;
  protected readonly convertDraftsToItems = convertDraftsToItems;

  withPatientName = input<boolean>(false);
  allowAddPrescription = input<boolean>(true); // Allow adding prescriptions by default
  allowEditPrescription = input<boolean>(true); // Allow editing prescriptions by default
  showPrescriptionActions = input<boolean>(true); // Show prescription item actions menu and quick guarantee buttons by default
  guaranteeItemUpdated = output<boolean>();

  protected readonly prescriptionsFilter = this.store.selectSignal(selectPrescriptionsFilters);
  protected readonly guaranteeApplications = this.store.selectSignal<GuaranteeApplication[]>(selectPrescriptionsGuarantees);
  protected readonly prescriptionDrafts =
    this.store.selectSignal<PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>[]>(selectPrescriptionDrafts);
  protected readonly targetPrescription = this.store.selectSignal(selectTargetPrescription);

  protected readonly expanded = signal<Set<string>>(new Set());
  protected readonly loading = signal<boolean>(false);
  protected readonly highlightedGuaranteeId = signal<string | null>(this.route.snapshot.queryParams['guarantee'] ?? null);
  protected readonly selectedPrescriptions = signal<Prescription[]>([]);
  protected readonly selectedPrescriptionType = signal<PrescriptionTypes | null>(null);
  protected readonly similarCheckTriggers = signal<Record<string, number>>({});

  constructor() {
    effect(() => {
      const searchFilter = this.prescriptionsFilter()?.search;

      if (searchFilter && searchFilter.trim()) {
        const allIds = new Set(this.guaranteeApplications().map(guarantee => guarantee.id));
        this.expanded.set(allIds);
      }
    });

    effect(() => {
      const target = this.targetPrescription();
      const guarantees = this.guaranteeApplications();

      if (target && guarantees.length > 0) {
        untracked(() => {
          const guaranteeWithPrescription = guarantees.find(guarantee => guarantee.prescriptions?.some(prescription => prescription.id === target.prescriptionId));

          if (guaranteeWithPrescription) {
            const currentExpanded = this.expanded();
            const newExpanded = new Set(currentExpanded);

            newExpanded.add(guaranteeWithPrescription.id);
            this.expanded.set(newExpanded);
          }
        });
      }
    });
  }

  protected isExpanded(guaranteeId: string): boolean {
    return this.expanded().has(guaranteeId);
  }

  protected checkGuaranteeSimilar(guaranteeId: string): void {
    this.similarCheckTriggers.update(triggers => ({ ...triggers, [guaranteeId]: (triggers[guaranteeId] ?? 0) + 1 }));
  }

  protected toggleExpansion(guaranteeId: string): void {
    const currentExpanded = this.expanded();
    const newExpanded = new Set(currentExpanded);

    if (newExpanded.has(guaranteeId)) newExpanded.delete(guaranteeId);
    else newExpanded.add(guaranteeId);
    this.expanded.set(newExpanded);
  }

  protected findUnprocessedPrescriptions(guarantee: GuaranteeApplication): boolean {
    const allPrescriptions = guarantee.prescriptions?.filter(isVisiblePrescription) || [];

    return allPrescriptions.some(prescription => prescription.guaranteeStatus === GuaranteeStatus.Unknown || prescription.status === PrescriptionStatus.WaitProcessing);
  }

  protected getFilteredPrescriptions(guarantee: GuaranteeApplication): Prescription[] {
    const allPrescriptions = guarantee.prescriptions?.filter(isVisiblePrescription) || [];
    const draftsForGuarantee = this.getDraftsForGuarantee(guarantee.id);
    const draftPrescriptionIds = new Set(draftsForGuarantee.map(draft => draft.id).filter(Boolean));

    return allPrescriptions.filter(prescription => !draftPrescriptionIds.has(prescription.id));
  }

  protected getAllPrescriptions(): Prescription[] {
    return this.guaranteeApplications()
      .flatMap(guarantee => guarantee.prescriptions || [])
      .filter(isVisiblePrescription);
  }

  protected hasExpandableContent(guarantee: GuaranteeApplication): boolean {
    return this.getFilteredPrescriptions(guarantee).length > 0 || this.getDraftsForGuarantee(guarantee.id).length > 0;
  }

  protected getDraftsForGuarantee(guaranteeId: string): PrescriptionFormValue<PharmacologicalTreatmentValue | GenericServiceValue | PersonalDoctorValue>[] {
    return this.prescriptionDrafts().filter(draft => draft.guaranteeId === guaranteeId);
  }

  protected checkDiagnosisCoverage(node: GuaranteeApplication, diagnosis: DiagnosisResponse) {
    return !!node.diagnosesCoverage.find(coverageItem => coverageItem.icd10Code === diagnosis.icd10?.code && coverageItem.isCovered);
  }

  protected processGuarantee(guarantee: GuaranteeApplication) {
    this.loading.set(true);
    const patientId = this.getMemberIdFromRoute();

    return this.guaranteesService
      .processGuaranteeApplication(patientId, guarantee.id)
      .pipe(take(1))
      .subscribe({
        next: () => this.store.dispatch(refreshPrescriptionsData({})),
        error: () => {
          this.snackBar.open('Не вдалось опрацювати зміни призначеннях', 'ОК');
          this.loading.set(false);
        },
        complete: () => this.loading.set(false),
      });
  }

  protected addPrescription(node: GuaranteeApplication, prescriptionType: PrescriptionTypes) {
    const patientId = this.getMemberIdFromRoute();
    let componentToOpen: ComponentType<unknown>;

    switch (prescriptionType) {
      case PrescriptionTypes.Drugs:
        componentToOpen = MedicationPrescriptionFormModalComponent;
        break;
      case PrescriptionTypes.PersonalDoctorAppointment:
        componentToOpen = PersonalDoctorPrescriptionFormModalComponent;
        break;
      default:
        componentToOpen = ServicePrescriptionFormModalComponent;
        break;
    }

    const dialogRef = this.dialog.open(componentToOpen, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      data: {
        prescription: null,
        prescriptionType,
        memberId: patientId,
        guaranteeApplicationId: node.id,
      },
    });

    this.handleAddPrescriptionClose(dialogRef);
  }

  protected addFreeFormPrescription(node: GuaranteeApplication) {
    const dialogRef = this.dialog.open(FreeFormModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: {
        prescription: null,
        memberId: this.getMemberIdFromRoute(),
        guaranteeApplicationId: node.id,
        mode: 'managed',
        isFreeForm: true,
      },
    });

    this.handleAddPrescriptionClose(dialogRef);
  }

  private handleAddPrescriptionClose(dialogRef: MatDialogRef<unknown>) {
    dialogRef
      .afterClosed()
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(result => {
          if (result) {
            this.store.dispatch(savePrescription());
            this.store.dispatch(refreshPrescriptionsData({}));
          }
          return of(null);
        }),
        take(1)
      )
      .subscribe({ complete: () => this.loading.set(false) });
  }

  protected updateGuarantee($event: boolean) {
    if ($event) this.store.dispatch(refreshPrescriptionsData({}));
  }

  protected navigateToMemberProfile(userId: string) {
    this.profileService.getClientProfileByUserId(userId).subscribe({
      next: result => this.router.navigate([`members/${result.id}`]),
      error: () => this.snackBar.open('Не вдалось здійснити перехід до профілю клієнта', 'OK', { duration: 5000 }),
    });
  }

  protected getMemberIdFromRoute() {
    return this.route.parent?.snapshot.params['id'];
  }

  protected onPrescriptionSelectionToggled(guaranteeId: string, { prescription, selected }: { prescription: Prescription; selected: boolean }) {
    const currentSelections = this.selectedPrescriptions();

    if (selected && (!this.selectedPrescriptionType() || this.selectedPrescriptionType() === prescription.type)) {
      this.selectedPrescriptionType.set(prescription.type);
      this.selectedPrescriptions.set([...currentSelections, prescription]);
    } else if (!selected) {
      const filtered = currentSelections.filter(p => p.id !== prescription.id);

      this.selectedPrescriptions.set(filtered);
      if (filtered.length === 0) this.selectedPrescriptionType.set(null);
    }
  }

  protected isPrescriptionSelectionEnabled(guaranteeId: string, prescription: Prescription): boolean {
    const selections = this.selectedPrescriptions();
    const selectedType = this.selectedPrescriptionType();
    const hasOtherGuaranteeSelections = selections.length > 0 && selections.every(p => this.getPrescriptionGuaranteeId(p) !== guaranteeId);

    return this.isPrescriptionSelectable(prescription) && (!selectedType || selectedType === prescription.type) && !hasOtherGuaranteeSelections;
  }

  private isPrescriptionSelectable(prescription: Prescription): boolean {
    const activeCount = prescription.planning?.filter(item => item.planningStatus === PrescriptionPlanningStatus.Completed).length || 0;

    return activeCount < prescription.count && prescription.used < prescription.count && prescription.type === PrescriptionTypes.LaboratoryService;
  }

  protected getSelectedPrescriptionsForGuarantee(guaranteeId: string): Prescription[] {
    return this.selectedPrescriptions().filter(p => this.getPrescriptionGuaranteeId(p) === guaranteeId);
  }

  protected hasSelectedPrescriptions(guaranteeId: string): boolean {
    return this.selectedPrescriptions().some(p => this.getPrescriptionGuaranteeId(p) === guaranteeId);
  }

  protected clearSelection(guaranteeId: string): void {
    const filtered = this.selectedPrescriptions().filter(p => this.getPrescriptionGuaranteeId(p) !== guaranteeId);

    this.selectedPrescriptions.set(filtered);
    if (filtered.length === 0) this.selectedPrescriptionType.set(null);
  }

  private getPrescriptionGuaranteeId(prescription: Prescription): string {
    return this.guaranteeApplications().find(g => g.prescriptions?.some(p => p.id === prescription.id))?.id || '';
  }

  protected createAppointmentForSelection(guaranteeId: string): void {
    switch (this.selectedPrescriptionType()) {
      case PrescriptionTypes.LaboratoryService:
        return this.createLaboratoryOrderForSelection(guaranteeId);
      // TODO: uncomment in scope of creating pharmacy orders
      // case PrescriptionTypes.Drugs:
      //   return this.createPharmacyOrderForSelection(guaranteeId);
      // TODO: uncomment in scope of creating appointment for multiple services
      // default:
      //   return this.createRegularAppointmentForSelection(guaranteeId);
    }
  }

  // TODO: need to be reviewed in scope of creating appointment for multiple services
  // private createRegularAppointmentForSelection(guaranteeId: string): void {
  //   const selectedPrescriptions = this.getSelectedPrescriptionsForGuarantee(guaranteeId);
  //   if (selectedPrescriptions.length === 0) return;
  //
  //   const appointmentType = this.selectedPrescriptionType() === PrescriptionTypes.InstrumentalDiagnostic ? AppointmentTypes.InstrumentalDiagnostic : AppointmentTypes.Appointment;
  //   const modalData: CreateAppointmentModalData = {
  //     type: CreateAppointmentModalType.ManualCreation,
  //     userId: selectedPrescriptions[0].userId,
  //     appointmentType,
  //     appointmentName: selectedPrescriptions.map(p => p.name).join(', '),
  //     eventPlace: AppointmentEventPlaceType.Hospital,
  //     prescriptions: selectedPrescriptions.map(p => ({
  //       id: p.id,
  //       franchise: p.franchise || 0,
  //       name: p.name,
  //     })),
  //   };
  //
  //   this.openModalAndHandleResult(
  //     this.dialog.open(CreateAppointmentModalComponent, {
  //       maxHeight: '90vh',
  //       width: '80vw',
  //       maxWidth: '768px',
  //       disableClose: true,
  //       data: modalData,
  //     }),
  //     guaranteeId
  //   );
  // }

  private createLaboratoryOrderForSelection(guaranteeId: string): void {
    const selectedPrescriptions = this.getSelectedPrescriptionsForGuarantee(guaranteeId);
    if (selectedPrescriptions.length === 0) return;

    const modalData: CreateLaboratoryOrderModalData = {
      type: CreateAppointmentModalType.ManualCreation,
      userId: selectedPrescriptions[0].userId,
      appointmentType: AppointmentTypes.LaboratoryService,
      appointmentName: selectedPrescriptions.map(p => p.name).join(', '),
      eventPlace: AppointmentEventPlaceType.Hospital,
      prescriptions: selectedPrescriptions.map(p => ({
        id: p.id,
        franchise: p.franchise || 0,
        name: p.name,
      })),
    };

    this.openModalAndHandleResult(
      this.dialog.open(CreateLaboratoryOrderModalComponent, {
        maxHeight: '90vh',
        width: '80vw',
        maxWidth: '768px',
        disableClose: true,
        data: modalData,
      }),
      guaranteeId
    );
  }

  // TODO: need to be reviewed in scope of pharmacy baskets creation
  // private createPharmacyOrderForSelection(guaranteeId: string): void {
  //   const selectedPrescriptions = this.getSelectedPrescriptionsForGuarantee(guaranteeId);
  //   if (selectedPrescriptions.length === 0) return;
  //
  //   const modalData: CreateAppointmentModalData = {
  //     type: CreateAppointmentModalType.ManualCreation,
  //     userId: selectedPrescriptions[0].userId,
  //     appointmentType: AppointmentTypes.Drugs,
  //     appointmentName: selectedPrescriptions.map(p => p.name).join(', '),
  //     eventPlace: AppointmentEventPlaceType.Hospital,
  //     prescriptions: selectedPrescriptions.map(p => ({
  //       id: p.id,
  //       franchise: p.franchise || 0,
  //       name: p.name,
  //     })),
  //   };
  //
  //   this.openModalAndHandleResult(
  //     this.dialog.open(CreateAppointmentModalComponent, {
  //       maxHeight: '90vh',
  //       width: '80vw',
  //       maxWidth: '768px',
  //       disableClose: true,
  //       data: modalData,
  //     }),
  //     guaranteeId
  //   );
  // }

  private openModalAndHandleResult(dialogRef: any, guaranteeId: string): void {
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: any) => {
        if (result) {
          this.clearSelection(guaranteeId);
          this.store.dispatch(refreshPrescriptionsData({}));
        }
      });
  }
}
